import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { api, ApiError, can, type ListName } from '../lib/api'
import { readValue, writeValue } from '../lib/prefs'
import { CallQuestions } from './CallQuestions'
import { Extensions } from './Extensions'
import { Members } from './Members'
import { MESSAGE_PAGES, Messages, type MessagePage } from './Messages'
import { Operations } from './Operations'
import { PhoneBook } from './PhoneBook'
import { Roles } from './Roles'
import { Users } from './Users'

/** The tabs that are a LIST — a name, an order, and a way of retiring an entry.
 *
 *  `certifications` IS ONE OF THEM AND IS NOT A `ListName`. The other three
 *  live on `/api/settings/{list}`, whose path segment picks a table from a hard
 *  whitelist of four; the qualifications the organization recognises are their
 *  own table with their own routes. What they share is the SCREEN — one add
 *  row, one list, one retire button — and that is what `Source` below is for.
 */
/* `natures` IS NOT ONE OF THEM ANY MORE. It is a list like these four, but its
 * screen is the question builder: the customer could not tell that a nature HAS
 * questions while the two sat on separate tabs -- "Call questions I think
 * should be under one tab. And when we click on the nature, you should have an
 * option of adding the questions" -- so `CallQuestions` owns the list, adds to
 * it, renames and retires through the same `/api/settings/natures` route this
 * editor calls, and draws each nature's questions beside it. */
const LIST_TABS = [
  'departments', 'resolutions', 'relationships', 'certifications',
] as const
type ListTab = typeof LIST_TABS[number]

function isListTab(tab: Tab): tab is ListTab {
  return (LIST_TABS as readonly string[]).includes(tab)
}

const TABS: { key: ListTab; label: string; blurb: string }[] = [
  {
    key: 'departments',
    label: 'Departments',
    blurb:
      'Optional per call type. Built now, switched off — today everything goes to everyone. Assign departments later and routing starts, with no rebuild.',
  },
  {
    key: 'resolutions',
    label: 'Resolutions',
    blurb: 'The quick-pick outcomes at close. Deliberately minimal to start.',
  },
  {
    key: 'relationships',
    label: 'Caller relationships',
    blurb:
      'Drives whether the caller’s own address fills in automatically at intake.',
  },
  {
    key: 'certifications',
    label: 'Certifications',
    blurb:
      'What the organization trains its members in. Which of these a member holds, and when each one expires, is on his own record.',
  },
]

type Tab =
  | ListName | 'certifications' | 'extensions' | 'roles'
  | 'phonebook' | 'members' | 'operations' | 'messages'

/** One row of one of those lists, whichever list it is.
 *
 *  TWO ROUTES SPELL THE SAME FACT DIFFERENTLY AND THIS IS THE ONE PLACE THE
 *  SPELLING IS TRANSLATED. `/api/settings/{list}` says retired with a
 *  `retired_at` TIMESTAMP; `/api/member-certifications` says it with an
 *  `active` boolean, following `member_groups`. Below this line nothing asks
 *  which list it is looking at — otherwise every row, every button and every
 *  empty state would carry the same two-way branch, and the third list added
 *  would get one of them backwards. */
type Row = {
  id: string
  name: string
  retired: boolean
  /** ACTIVE members holding it — certifications only, because nothing holds a
   *  nature of call. It is what somebody wants to know before retiring one. */
  holders?: number
  /** Caller relationships only. */
  fills_caller_address?: boolean | null
}

/** Where one list's rows come from, and what its three writes do.
 *
 *  ONE EDITOR, MORE THAN ONE ROUTE. The alternative was a second list screen
 *  for the certifications — the same add row, the same rows, the same Retire
 *  button — and the two would then have drifted about what an empty name does
 *  or whether a retired entry is hidden. What differs between these lists is
 *  three functions; what they have in common is a screen.
 *
 *  THE PERMISSION IS NOT HERE, it is on the tab (`ALL_TABS`), because the key
 *  that draws a door has to be the key that opens it. These three carry
 *  `settings.lists` and the certifications carry `settings.members` — the same
 *  key the roster itself carries, and not a new leaf: whoever says which
 *  qualifications the organization recognises is whoever says who is on the
 *  roster, and section 19 forbids a tick for a distinction the product draws
 *  nowhere. */
type Source = {
  rows: (includeRetired: boolean) => Promise<Row[]>
  create: (name: string, order: number) => Promise<unknown>
  /** CORRECTING A SPELLING IS THE ONLY WAY OUT OF ONE. Both routes have taken
   *  a name since they were written and nothing called either — the settings
   *  lists' own module says in as many words that these four "can be added to,
   *  renamed, or retired", and the screen kept two of the three.
   *
   *  It matters most for the newest list. Retire-and-re-add is not a
   *  workaround for a misspelt certification: retiring strands every member who
   *  holds it on the row with the wrong name, the name stays taken because the
   *  unique index ignores `active`, and each holder then has to be re-attached
   *  by hand. Renaming rewrites no history either way — an incident freezes the
   *  nature it was closed with, and a holding points at the catalogue row
   *  rather than copying its name. */
  rename: (row: Row, name: string) => Promise<unknown>
  setRetired: (row: Row, retired: boolean) => Promise<unknown>
}

function sourceFor(tab: ListTab): Source {
  if (tab === 'certifications') {
    return {
      rows: async (includeRetired) =>
        (await api.certifications(includeRetired)).map((c) => ({
          id: c.id, name: c.name, retired: !c.active, holders: c.holders,
        })),
      // `sort_order` IS DELIBERATELY NOT SENT, so every certification keeps the
      // server's default of 0 and `ORDER BY sort_order, name` falls through to
      // the name. Sending a count would make it creation order, which is an
      // order nobody on this screen can correct — the same call
      // `MemberGroupsDialog` makes, and for the same reason: section 19 forbids
      // inventing a control for a distinction the product does not draw.
      create: (name) => api.certificationCreate({ name }),
      rename: (row, name) => api.certificationUpdate(row.id, { name }),
      // RETIRED, NEVER DELETED, and there is no delete route to call. Members
      // who already hold it keep it — that asymmetry is the whole point — and
      // the foreign key is ON DELETE RESTRICT besides.
      setRetired: (row, retired) =>
        api.certificationUpdate(row.id, { active: !retired }),
    }
  }
  const list: ListName = tab
  return {
    rows: async (includeRetired) =>
      (await api.settingsList(list, includeRetired)).map((it) => ({
        id: it.id,
        name: it.name,
        retired: it.retired_at !== null,
        fills_caller_address: it.fills_caller_address,
      })),
    create: (name, order) => api.settingsCreate(list, { name, sort_order: order }),
    rename: (row, name) => api.settingsUpdate(list, row.id, { name }),
    setRetired: (row, retired) => api.settingsUpdate(list, row.id, { retired }),
  }
}

/** Every page and who may see each. The ORDER on screen is `PAGE_ORDER`'s,
 *  under `GROUP_ORDER`'s headings (1 Nov 2026).
 *
 *  THE GATING IS PER TAB, NOT ON THE SECTION, and that is the whole reason
 *  this list exists rather than a `can('settings')` around the lot. The phone
 *  book and the roster used to be their own items in the top bar and were
 *  deliberately UNGATED -- CLAUDE.md: "A dispatcher adds a caller he has just
 *  spoken to; making that an administrator's job is how a phone book stays
 *  empty." Moving them in here behind one Settings check would have taken both
 *  away from every dispatcher in the organization, silently, as a side effect
 *  of tidying a menu.
 *
 *  So a Dispatcher opening Settings sees exactly two tabs. An administrator
 *  sees all of them.
 *
 *  A FULL-HEIGHT tab claims the window and scrolls itself (the sheets and
 *  Operations); the rest sit in a scrolling workspace of cards. */
type TabDef = {
  key: Tab
  label: string
  /** The heading it sits under in the side column (1 Nov 2026). */
  group: Group
  /** Its picture in the side column, the only thing shown when it is narrow. */
  icon: keyof typeof ICONS
  /** Nothing means anybody may see it. */
  needs?: string
  /** Any ONE of these is enough. For a tab that two different permissions
   *  each give a reason to open. */
  needsAny?: string[]
  /** It brings its own scroll container and wants the whole window. */
  full?: boolean
}

/** Whether this role may see one tab. One definition, because the nav asks the
 *  same question the tab bar asks and the two answering differently is a
 *  button that opens onto nothing. */
function maySee(t: TabDef): boolean {
  if (t.needsAny) return t.needsAny.some(can)
  return !t.needs || can(t.needs)
}

const ALL_TABS: TabDef[] = [
  // THE SCREEN IS ADMINISTRATIVE; CREATING A RECORD IS NOT. A dispatcher
  // adds a caller from the card at intake and never comes here, which is the
  // customer's own division: "it's only made to make sure that the system has
  // all the contacts."
  // `phonebook.import` as well as View, because the ONLY import control lives
  // on this screen. A role given Bulk import and not View held a permission
  // with no door -- indistinguishable from a feature nobody built. The sheet
  // itself checks View before drawing rows.
  { key: 'phonebook', label: 'Phone book', group: 'People', icon: 'book', needsAny: ['phonebook.view', 'phonebook.import'], full: true },
  // THE ROSTER IS AN ADMINISTRATOR'S NOW. The customer, 16 Sep 2026: a
  // dispatcher "should not have access to anything, only to dispatch". The
  // same key that edits the roster draws it, and the listing a role without it
  // is handed is who and on duty only (`PICKER_FIELDS` in routes/members.py).
  // `settings.member_documents` TOO, because the record dialog on this tab is
  // the only door identity documents have -- a role given documents and not
  // roster editing would otherwise hold a permission with nothing behind it.
  { key: 'members', label: 'Members', group: 'People', icon: 'people', needsAny: ['settings.members', 'settings.member_documents'], full: true },
  // THE LEAF, NOT THE BRANCH. `settings` is stored by `permissions.clean` for
  // any settings leaf at all, so this drew the tab for a roster manager and a
  // lists coordinator -- and then every control on it answered "Changing these
  // needs the Settings permission", which they could see they had. The key
  // `app/routes/org_settings.py` actually reads is the leaf.
  { key: 'operations', label: 'Operations', group: 'System', icon: 'gear', needs: 'settings.organization', full: true },
  // EVERYTHING A MEMBER'S PHONE RECEIVES, IN ONE PLACE (057): the header and
  // footer, which lived on Operations, and what the member who accepts is
  // sent. The same key as Operations because the same route serves both.
  // "CALL MESSAGES" SINCE 1 NOV 2026 (his Q1 A), with its four pages listed
  // under it in the column: the heading already says Messages, and the
  // message settings still to come sit beside it under names of their own.
  { key: 'messages', label: 'Call messages', group: 'Messages', icon: 'chat', needs: 'settings.organization', full: true },
  // ONE TAB, AND IT USED TO BE TWO. "Nature of call" was the list and "Call
  // questions" was the tree, which made the questions read as a global thing
  // rather than as something a nature HAS -- the customer, looking at
  // seventy-odd natures he had entered: "Now I think your questions are
  // globally. It's still too hard for me to figure out what you built."
  //
  // THE SAME KEY EITHER WAY, and not a new leaf. `settings.lists` already means
  // "the organization's own lists"; both halves of the merged screen check it
  // on the server -- `/api/settings/natures` and `/api/questions/...` -- and
  // section 19 forbids a tick for a distinction the product draws nowhere.
  // Merging the two tabs removed the last place one might have been drawn.
  { key: 'natures', label: 'Nature of call', group: 'Calls', icon: 'list', needs: 'settings.lists', full: true },
  { key: 'departments', label: 'Departments', group: 'Calls', icon: 'org', needs: 'settings.lists' },
  { key: 'resolutions', label: 'Resolutions', group: 'Calls', icon: 'check', needs: 'settings.lists' },
  { key: 'relationships', label: 'Caller relationships', group: 'Calls', icon: 'link', needs: 'settings.lists' },
  // `settings.members` AND NOT `settings.lists`, because that is the key
  // `app/routes/member_certifications.py` actually checks. A tab drawn on one
  // key whose every control answers 403 for another is what the Operations tab
  // did for a roster manager, and it is worse than no tab: a granted permission
  // and a broken screen look identical from the far side of a refusal.
  { key: 'certifications', label: 'Certifications', group: 'People', icon: 'badge', needs: 'settings.members' },
  { key: 'extensions', label: 'Extensions & numbers', group: 'System', icon: 'phone', needs: 'settings.extensions' },
  { key: 'roles', label: 'Users & roles', group: 'System', icon: 'key', needs: 'settings.roles' },
]

/** THE SIDE COLUMN'S HEADINGS, in order (1 Nov 2026, the customer's Q1 A),
 *  and the pages under them as `settings-side-mockup.html` showed them. A
 *  page missing from `PAGE_ORDER` goes last, never first: the first page
 *  shown is the one Settings opens on. */
type Group = 'People' | 'Calls' | 'Messages' | 'System'
const GROUP_ORDER: Group[] = ['People', 'Calls', 'Messages', 'System']
const PAGE_ORDER: Tab[] = [
  'phonebook', 'members', 'certifications',
  'natures', 'resolutions', 'relationships', 'departments',
  'messages',
  'operations', 'extensions', 'roles',
]

/** A small drawn picture per page: the same in both themes (it is the
 *  text colour), and on Windows an emoji would be a smudge. */
const ICONS = {
  book: 'M5 4h11a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3zM5 17a3 3 0 0 1 3-3h11M12 6.7a1.8 1.8 0 1 1 0 3.6a1.8 1.8 0 1 1 0-3.6',
  people: 'M9 5a3 3 0 1 1 0 6a3 3 0 1 1 0-6M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6M17 6.6a2.4 2.4 0 1 1 0 4.8a2.4 2.4 0 1 1 0-4.8M15.5 14.2c3 .2 5.5 2.6 5.5 5.8',
  badge: 'M6 5h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2M9 9a2 2 0 1 1 0 4a2 2 0 1 1 0-4M6.5 16c.6-1.6 1.6-2.4 2.5-2.4s1.9.8 2.5 2.4M14 10h4M14 13h3',
  list: 'M9 6h11M9 12h11M9 18h11M4.5 5a1 1 0 1 1 0 2a1 1 0 1 1 0-2M4.5 11a1 1 0 1 1 0 2a1 1 0 1 1 0-2M4.5 17a1 1 0 1 1 0 2a1 1 0 1 1 0-2',
  check: 'M12 3.5a8.5 8.5 0 1 1 0 17a8.5 8.5 0 1 1 0-17M8.3 12.3l2.6 2.6 4.9-5.4',
  link: 'M7 9a3 3 0 1 1 0 6a3 3 0 1 1 0-6M17 9a3 3 0 1 1 0 6a3 3 0 1 1 0-6M10 12h4',
  org: 'M10 3.5h4a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1M4 15.5h4a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1M16 15.5h4a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1M12 8.5v3.5M6 15.5V12h12v3.5',
  chat: 'M4 5h16v11H9l-5 4zM8 9.5h8M8 12.5h5',
  gear: 'M12 9a3 3 0 1 1 0 6a3 3 0 1 1 0-6M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7M16.8 7.2l1.7-1.7',
  phone: 'M6 3.5h3l1.5 4-2 1.3a11 11 0 0 0 6.7 6.7l1.3-2 4 1.5v3a2 2 0 0 1-2 2A16.5 16.5 0 0 1 4 5.5a2 2 0 0 1 2-2z',
  key: 'M8 8.5a3.5 3.5 0 1 1 0 7a3.5 3.5 0 1 1 0-7M11.5 12H21M17.5 12v3M20 12v2',
  // The four Call messages pages (1 Nov 2026).
  mega: 'M4 10v4h3l7 4V6L7 10zM17.5 9a4 4 0 0 1 0 6',
  going: 'M9 4a3 3 0 1 1 0 6a3 3 0 1 1 0-6M3 20c0-3.3 2.7-6 6-6c1.4 0 2.6.4 3.6 1.2M15 17h6M18.5 14.5L21 17l-2.5 2.5',
  heart: 'M12 19.5s-7.5-4.6-7.5-10A4.2 4.2 0 0 1 12 7a4.2 4.2 0 0 1 7.5 2.5c0 5.4-7.5 10-7.5 10z',
  again: 'M19 12a7 7 0 1 1-2.1-5M19.5 4.5v4h-4',
  chevron: 'M9 6l6 6-6 6',
} as const

/** The pictures of the pages listed under Call messages. */
const MESSAGE_ICONS: Record<MessagePage, keyof typeof ICONS> = {
  broadcast: 'mega', going: 'going', thanks: 'heart', rebroadcast: 'again',
}

/** Wide (the names) or narrow (the pictures only): the customer's Q2 B, kept
 *  per computer like every other matter of taste (`lib/prefs`). */
const SIDE_PREF = 'settings.side'


/** Whether this role can reach ANY tab of this section.
 *
 *  The nav asks before drawing the button. Every tab carries its own key now,
 *  the roster included, so a dispatcher with none of them has no Settings
 *  button at all rather than one opening onto an empty section. */
export function hasSettingsTabs(): boolean {
  return ALL_TABS.some(maySee)
}

export function Settings() {
  const visible = useMemo(() => {
    const at = (k: Tab) => { const i = PAGE_ORDER.indexOf(k); return i < 0 ? PAGE_ORDER.length : i }
    return ALL_TABS.filter(maySee).sort((a, b) => at(a.key) - at(b.key))
  }, [])
  const [narrow, setNarrow] = useState(() => readValue(SIDE_PREF, ['wide', 'narrow'], 'wide') === 'narrow')
  const toggleNarrow = () => {
    const next = !narrow
    setNarrow(next)
    writeValue(SIDE_PREF, next ? 'narrow' : 'wide')
  }
  // The first tab this person can actually see. Defaulting to a fixed one
  // would open a dispatcher on a tab that is not in their list.
  const [tab, setTab] = useState<Tab>(() => visible[0]?.key ?? 'phonebook')
  /** CALL MESSAGES' OWN PAGES (1 Nov 2026): which one is open -- the tabs on
   *  top and the list in the column both set it -- whether that list is
   *  open, and which pages hold something not saved. */
  const [msgPage, setMsgPage] = useState<MessagePage>('broadcast')
  const [msgOpen, setMsgOpen] = useState(() => tab === 'messages')
  const [msgDirty, setMsgDirty] = useState<MessagePage[]>([])
  /** Mounted from its first visit on, and only hidden after: what is typed
   *  there must survive a look at the phone book. */
  const [msgSeen, setMsgSeen] = useState(() => tab === 'messages')
  /** Another page: the list under Call messages shuts, as the mock-up had it. */
  function goTo(key: Tab) {
    setTab(key)
    if (key === 'messages') { setMsgSeen(true); setMsgOpen(true) } else setMsgOpen(false)
  }
  const [items, setItems] = useState<Row[]>([])
  const [showRetired, setShowRetired] = useState(false)
  const [newName, setNewName] = useState('')
  /** Which row is being renamed, and what has been typed over it so far. */
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const active = TABS.find((t) => t.key === tab)

  async function load() {
    if (!isListTab(tab)) return
    try {
      setItems(await sourceFor(tab).rows(showRetired))
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the list.')
    }
  }

  // ONLY FOR A TAB THAT IS A LIST. This used to skip Extensions and Roles by
  // name, which meant Phone book, Members and Operations -- all three added
  // later -- fired a request for `/api/settings/phonebook`, a path segment that
  // is matched against a four-table whitelist and can never be one of them.
  useEffect(() => {
    // A RENAME DOES NOT SURVIVE THE LIST IT WAS OPENED ON. `editing` is an id
    // and a string, and both lists have ids -- left alone, switching tabs mid
    // rename would leave the box open over whichever row happened to match.
    setEditing(null)
    if (!isListTab(tab)) return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, showRetired])

  async function add(e: FormEvent) {
    e.preventDefault()
    if (!newName.trim() || !isListTab(tab)) return
    setBusy(true)
    try {
      await sourceFor(tab).create(newName.trim(), items.length)
      // Cleared INSIDE the write: a duplicate name comes back naming it, and
      // the words have to still be in the box to be corrected.
      setNewName('')
      setError(null)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not add that.')
    } finally {
      setBusy(false)
    }
  }

  /** Whether choosing this relationship fills the incident's address from the
   *  caller's own record. PLAN.html: caller relationships "drive whether the
   *  caller's own address auto-fills at intake".
   *
   *  NOT ON `Source`, deliberately. It belongs to exactly one of these lists
   *  and always will -- nothing else on this screen has a second flag -- so it
   *  calls its own route directly rather than making every other list carry a
   *  method that does nothing. The control that reaches it is drawn for that
   *  tab alone. */
  async function setFills(row: Row, on: boolean) {
    try {
      await api.settingsUpdate('relationships', row.id, {
        fills_caller_address: on,
      })
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save that.')
    }
  }

  /** Start correcting a name, through the same question every other exit asks.
   *
   *  A HALF-TYPED NAME IS UNSAVED WORK. Pressing Rename one row further down
   *  used to be the one way to discard it without a word — the guard
   *  `MemberGroupsDialog` learned to put on exactly this. Cancel on the row
   *  itself stays unguarded: that means "forget this name", said about the name
   *  in front of you. */
  function startRename(row: Row) {
    const open = editing && items.find((r) => r.id === editing.id)
    if (open && editing && editing.name.trim() !== open.name && !confirm(
      `Discard the new name typed for ${open.name}?`
      + ' Everything already saved stays as it is.')) return
    setEditing({ id: row.id, name: row.name })
  }

  async function rename() {
    if (!editing || !isListTab(tab)) return
    const row = items.find((r) => r.id === editing.id)
    const typed = editing.name.trim()
    if (!row || !typed) return
    // Nothing to send is a 400 on one of these routes and a wasted write on the
    // other, and neither is a useful answer to pressing Save on a name nobody
    // changed.
    if (typed === row.name) { setEditing(null); return }
    setBusy(true)
    try {
      await sourceFor(tab).rename(row, typed)
      // Closed INSIDE the write, like the add box is cleared inside it: a
      // duplicate name comes back naming it, and the rejected name has to still
      // be in the box to be corrected.
      setEditing(null)
      setError(null)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not rename that.')
    } finally {
      setBusy(false)
    }
  }

  async function setRetired(row: Row, retired: boolean) {
    if (!isListTab(tab)) return
    try {
      await sourceFor(tab).setRetired(row, retired)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not update that.')
    }
  }

  return (
    /* NOT A SCROLLING WORKSPACE, because several of these pages are
       full-window sheets that claim the height themselves. The column on the
       left stays put and each page brings its own scroll container --
       `.sheetpage` for the sheets, `.workspace` for the lists -- inside
       `.setmain`, a flex column with a real height. */
    <div className="settingspage setsplit">
      {/* THE PAGES IN A COLUMN ON THE LEFT, under four headings (1 Nov 2026,
          the customer: the two rows of buttons across the top went, so the
          page under them gets the whole height). Each heading is drawn only
          when this role can see a page under it. */}
      <nav className={`setside ${narrow ? 'narrow' : ''}`} aria-label="Settings pages">
        {!narrow && <h2>Settings</h2>}
        {GROUP_ORDER.map((g) => {
          const pages = visible.filter((t) => t.group === g)
          if (!pages.length) return null
          return (
            <div className="setgroup" key={g}>
              {!narrow && <div className="setgrouplbl">{g}</div>}
              {pages.map((t) => t.key === 'messages' ? (
                /* ONE BUTTON WITH ITS PAGES UNDER IT (1 Nov 2026, the
                   customer: "one button for messages. When you click on it,
                   it will have four options under it"). From another page it
                   opens Call messages where it was left; on Call messages it
                   opens and shuts the list. Narrow, the list is not drawn --
                   the tabs on top of the page do the same. A dot says
                   something there is not saved whenever the list that would
                   show it is shut. */
                <div key={t.key}>
                  <button
                    type="button"
                    className={`setitem setparent ${tab === 'messages' ? 'here' : ''} ${msgOpen && !narrow ? 'open' : ''}`}
                    aria-expanded={narrow ? undefined : msgOpen}
                    aria-current={tab === 'messages' && (narrow || !msgOpen) ? 'page' : undefined}
                    title={t.label}
                    aria-label={narrow ? t.label : undefined}
                    onClick={() => (tab === 'messages' ? setMsgOpen((o) => !o) : goTo('messages'))}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[t.icon]} /></svg>
                    {!narrow && <span>{t.label}</span>}
                    {/* Drawn whenever something is not saved; CSS hides it while the
                        list under it is open and showing the dots itself. */}
                    {msgDirty.length > 0 && <i className="setdot" title="Changes not saved" />}
                    {!narrow && <svg className="setchev" viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS.chevron} /></svg>}
                  </button>
                  {msgOpen && !narrow && (
                    <div className="setsub">
                      {MESSAGE_PAGES.map((p) => {
                        const on = tab === 'messages' && msgPage === p.key
                        return (
                          <button key={p.key} type="button" className={`setitem ${on ? 'on' : ''}`}
                                  aria-current={on ? 'page' : undefined} title={p.label}
                                  onClick={() => { setMsgPage(p.key); goTo('messages') }}>
                            <svg viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[MESSAGE_ICONS[p.key]]} /></svg>
                            <span>{p.label}</span>
                            {msgDirty.includes(p.key) && <i className="setdot" title="Changes not saved" />}
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <button
                  key={t.key}
                  type="button"
                  className={`setitem ${t.key === tab ? 'on' : ''}`}
                  aria-current={t.key === tab ? 'page' : undefined}
                  // NAMED ON HOVER AND TO A SCREEN READER WHEREVER ONLY THE
                  // PICTURE SHOWS -- narrow, or a narrow window's strip.
                  title={t.label}
                  aria-label={narrow ? t.label : undefined}
                  onClick={() => goTo(t.key)}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[t.icon]} /></svg>
                  {!narrow && <span>{t.label}</span>}
                </button>
              ))}
            </div>
          )
        })}
        <button type="button" className="setnarrow" onClick={toggleNarrow} aria-pressed={narrow}
                aria-label={narrow ? 'Make the column wide' : 'Make the column narrow'}
                title={narrow ? 'Make the column wide' : undefined}>
          {narrow ? '»' : '« Make the column narrow'}
        </button>
      </nav>

      <div className="setmain">

      {/* CALL MESSAGES STAYS MOUNTED once opened, hidden while another page
          is looked at, so its four pages' typing survives the look. */}
      {msgSeen && (
        <Messages page={msgPage} onPage={setMsgPage} onDirty={setMsgDirty} hidden={tab !== 'messages'} />
      )}

      {/* The three that came in from the top bar. Each already owns the whole
          window and scrolls itself, so they are rendered bare. */}
      {tab === 'phonebook' ? <PhoneBook />
        : tab === 'members' ? <Members />
        : tab === 'operations' ? <Operations />
        : tab === 'messages' ? null
        : tab === 'natures' ? (
          /* IT CLAIMS THE WINDOW AND SCROLLS ITS OWN TWO PANES, like the
             sheets, so it is rendered bare. The list editor below is for a tab
             that IS a list — one add row, one column of rows — and this one is
             a list whose rows open a tree beside them. */
          <CallQuestions />
        ) : tab === 'extensions' ? (
          <div className="workspace"><Extensions /></div>
        ) : tab === 'roles' ? (
          <div className="workspace">
        {/* ONE TAB, TWO SECTIONS, in this order. A user is created WITH a role,
           so the thing you are choosing from should already be on the screen
           above -- and "who can sign in" is the question somebody comes here
           with far more often than "what may that role reach". (It was bare
           text inside the JSX until 1 Nov 2026, and drawn on the page.) */}
          <div className="card">
            <Users />
          </div>
          <div className="card">
            <h2>Roles</h2>
            <p className="blurb">
              No fixed ranks — you make named roles, tick what each can reach,
              and give one to each login. The list of ticks is the list of
              things the system actually checks, so it grows as the product
              does.
            </p>
            <Roles />
          </div>
          </div>
        ) : (
          <div className="workspace">
      <div className="card">
        <h2>{active!.label}</h2>
        <p className="blurb">{active!.blurb}</p>

        {error && <div className="notice">{error}</div>}

        <form className="addrow" onSubmit={add}>
          {/* BOUNDED AT THE 120 BOTH ROUTES ENFORCE, like the rename box
              below. Overshooting it is not a sentence anybody can act on:
              FastAPI answers a `max_length` violation with a 422 whose `detail`
              is an ARRAY, and `request()` in lib/api.ts puts `detail` straight
              into the message — so a pasted paragraph comes back as
              "[object Object]". */}
          <input
            value={newName}
            maxLength={120}
            placeholder={`Add a ${active!.label.toLowerCase()}…`}
            onChange={(e) => setNewName(e.target.value)}
          />
          <button className="primary compact" disabled={busy || !newName.trim()}>
            Add
          </button>
        </form>

        {items.length === 0 ? (
          <div className="listempty">
            Nothing here yet.
            {tab === 'certifications'
              && ' Until there is one, nothing can be recorded against a member.'}
          </div>
        ) : (
          items.map((it) => (editing?.id === it.id ? (
            /* A FORM, so Enter saves — the shape `RenameRow` in
               `MemberGroups.tsx` already has. Nothing here is nested inside
               another form, so Enter has nothing else to submit. */
            <form
              className="callbar"
              key={it.id}
              style={{ marginBottom: 0, padding: '4px 0' }}
              onSubmit={(e) => { e.preventDefault(); rename() }}
              onKeyDown={(e) => {
                if (e.key !== 'Escape') return
                setEditing(null)
              }}
            >
              <input
                className="grow"
                value={editing.name}
                autoFocus
                maxLength={120}
                aria-label={`Rename ${it.name}`}
                onChange={(e) => setEditing({ id: it.id, name: e.target.value })}
              />
              <button
                className="ghost tiny nogrow"
                type="submit"
                disabled={busy || !editing.name.trim()}
              >
                Save
              </button>
              <button
                className="ghost tiny nogrow"
                type="button"
                onClick={() => setEditing(null)}
              >
                Cancel
              </button>
            </form>
          ) : (
            <div className={`listrow ${it.retired ? 'retired' : ''}`} key={it.id}>
              <span className="nm">{it.name}</span>
              {it.retired && <span className="tag">retired</span>}

              {/* The flag this screen's own blurb promises drives behaviour.
                  It could be set through the API and there was no control for
                  it, so the four seeded relationships worked and every one the
                  customer added himself silently never filled -- with no
                  visible reason, on a screen telling him it would. */}
              {tab === 'relationships' && !it.retired && (
                <label className="check inline" title="Only when the caller is
                  reporting something at their own address. A passer-by must
                  never bring his home address to somebody else's emergency.">
                  <input
                    type="checkbox"
                    checked={!!it.fills_caller_address}
                    onChange={(e) => setFills(it, e.target.checked)}
                  />
                  fills the caller's address
                </label>
              )}

              {/* HOW MANY PEOPLE HOLD IT, which is the question somebody has in
                  front of them when they reach for Retire. Counted on the
                  server and ACTIVE members only, so the figure beside a
                  qualification and the people it actually covers cannot
                  disagree. */}
              {it.holders !== undefined && (
                <span className="lbl" style={{ marginLeft: 'auto' }}>
                  {it.holders === 0 ? 'nobody yet'
                    : it.holders === 1 ? '1 member' : `${it.holders} members`}
                </span>
              )}

              {/* EXACTLY ONE THING IN A ROW MAY ABSORB THE FREE SPACE, and
                  `button.tiny` claims it by default — so the first of these
                  right-hand items takes it and everything after it is `nogrow`.
                  Two of them would split the gap and leave the holders count
                  floating in the middle of the row rather than sitting with the
                  buttons. */}
              <button
                className={it.holders === undefined ? 'ghost tiny' : 'ghost tiny nogrow'}
                onClick={() => startRename(it)}
              >
                Rename
              </button>

              <button
                className="ghost tiny nogrow"
                onClick={() => setRetired(it, !it.retired)}
              >
                {it.retired ? 'Restore' : 'Retire'}
              </button>
            </div>
          )))
        )}

        <label className="showretired">
          <input
            type="checkbox"
            checked={showRetired}
            onChange={(e) => setShowRetired(e.target.checked)}
          />
          Show retired
        </label>

        {/* Deletion is not offered anywhere on this screen, on purpose — but
            WHAT SURVIVES A RETIREMENT IS NOT THE SAME FACT FOR BOTH KINDS OF
            LIST, and one sentence covering both would be wrong about one of
            them. An incident freezes the nature it was closed with; a holding
            points at the certification rather than copying its name, so
            retiring one leaves everybody who holds it holding it. */}
        {tab === 'certifications' ? (
          <p className="footnote">
            Retired, never deleted — members who already hold one keep it, and
            it stops being offered on anybody new.
          </p>
        ) : (
          <p className="footnote">
            Entries are retired, never deleted — a resolution retired next year
            must still read correctly on an incident from this one. Renaming is
            safe too: incidents freeze the label they were closed with.
          </p>
        )}
      </div>
          </div>
        )}
      </div>
    </div>
  )
}
